import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  VisibilityState,
  ColumnFiltersState,
  SortingState,
  RowSelectionState,
  ColumnSizingState,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  enableRowSelection?: boolean;
  enableSorting?: boolean;
  enableColumnFilters?: boolean;
  enableColumnResizing?: boolean;
  className?: string;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: (columnSizing: ColumnSizingState) => void;
  emptyMessage?: string;
  onRowClick?: (row: TData) => void;
}

export function DataTable<TData>({
  data,
  columns,
  enableRowSelection = false,
  enableSorting = true,
  enableColumnFilters = true,
  enableColumnResizing = false,
  className,
  rowSelection,
  onRowSelectionChange,
  columnVisibility,
  onColumnVisibilityChange,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  onSortingChange,
  columnSizing,
  onColumnSizingChange,
  emptyMessage = "No results found.",
  onRowClick,
}: DataTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    state: {
      ...(rowSelection !== undefined && { rowSelection }),
      ...(columnVisibility !== undefined && { columnVisibility }),
      ...(columnFilters !== undefined && { columnFilters }),
      ...(sorting !== undefined && { sorting }),
      ...(columnSizing !== undefined && { columnSizing }),
    },
    enableRowSelection,
    onRowSelectionChange,
    onColumnVisibilityChange,
    onColumnFiltersChange,
    onSortingChange,
    onColumnSizingChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: enableColumnFilters ? getFilteredRowModel() : undefined,
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    columnResizeMode: enableColumnResizing ? "onChange" : undefined,
  });

  return (
    <div className={cn("w-full rounded-md border", enableColumnResizing ? "overflow-visible" : "overflow-auto", className)}>
      <div className={enableColumnResizing ? "overflow-x-auto" : ""}>
        <Table enableColumnResizing={enableColumnResizing} style={{ width: enableColumnResizing ? table.getCenterTotalSize() : '100%', minWidth: '100%' }}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{ width: header.getSize() }}
                  className={cn(
                    "relative",
                    enableColumnResizing && header.column.getCanResize() && "resize-handle"
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                   {enableColumnResizing && header.column.getCanResize() && (
                     <div
                       onMouseDown={header.getResizeHandler()}
                       onTouchStart={header.getResizeHandler()}
                       className="absolute right-0 top-0 h-full w-2 cursor-col-resize bg-transparent active:bg-primary/50 transition-colors"
                       style={{
                         transform: header.column.getIsResizing()
                           ? `translateX(${table.getState().columnSizingInfo.deltaOffset}px)`
                           : "",
                         pointerEvents: 'auto',
                         zIndex: 10
                       }}
                     />
                   )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className={cn(
                  onRowClick && "cursor-pointer hover:bg-transparent"
                )}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        </Table>
      </div>
    </div>
  );
}